// src/components/CustomerList.jsx
import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link, useNavigate } from "react-router-dom";
import { fetchCostumers } from "../actions/authActions";

import HotToastMessage from "./ui/HotToastMessage";
import ConfirmModal from "./ui/ConfirmModal";
import PaginationControls from "./PaginationControls";

import { Search, Trash2 } from "lucide-react";

const CustomerList = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  // ---- Auth guard (same style as CottageList)
  const auth = useSelector((state) => state.auth);
  useEffect(() => {
    if (auth.isAuthenticated === false) navigate("/");
  }, [auth.isAuthenticated, navigate]);

  // ---- Redux state
  const costumerstate = useSelector((state) => state.costumers);
  const { costumerList, customersLoading, customersError } = costumerstate || {
    costumerList: [],
    customersLoading: false,
    customersError: null,
  };

  // ---- Local UI state
  const [searchText, setSearchText] = useState("");
  const [query, setQuery] = useState("");
  const [sortConfig, setSortConfig] = useState({ ordering: "" });

  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);

  const [selectedCustomers, setSelectedCustomers] = useState([]);
  const [areAllSelected, setAreAllSelected] = useState(false);

  const [toast, setToast] = useState({ message: "", type: "info" });
  const [showModal, setShowModal] = useState(false);

  // حذف لوکال – فقط برای UI (برای اتصال به API بعداً)
  const [deletedIds, setDeletedIds] = useState([]);

  // ---- Debounced search
  useEffect(() => {
    const handler = setTimeout(() => {
      setQuery(searchText.trim());
      setCurrentPage(1);
    }, 300);
    return () => clearTimeout(handler);
  }, [searchText]);

  // ---- Fetch customers on mount & search
  useEffect(() => {
    dispatch(fetchCostumers(query));
  }, [dispatch, query]);

  // ---- Clean customer list (remove locally deleted)
  const customers = useMemo(
    () => (Array.isArray(costumerList) ? costumerList : []).filter((c) => !deletedIds.includes(c.id)),
    [costumerList, deletedIds]
  );

  // ---- Client-side filter (safe)
  const filtered = useMemo(() => {
    const q = (query || "").trim();
    if (!q) return customers;

    return customers.filter((c) => {
      const name = String(c.full_name || "");
      const phone = String(c.phone_number || "");
      const national = String(c.national_code || "");
      return name.includes(q) || phone.includes(q) || national.includes(q);
    });
  }, [customers, query]);

  // ---- Sort
  const sorted = useMemo(() => {
    const ord = sortConfig.ordering;
    if (!ord) return filtered;

    const desc = ord.startsWith("-");
    const key = desc ? ord.slice(1) : ord;
    const arr = [...filtered];

    arr.sort((a, b) => {
      const va = (a?.[key] ?? "").toString();
      const vb = (b?.[key] ?? "").toString();
      if (va < vb) return desc ? 1 : -1;
      if (va > vb) return desc ? -1 : 1;
      return 0;
    });
    return arr;
  }, [filtered, sortConfig]);

  // ---- Pagination
  const count = sorted.length;
  const totalPages = Math.max(1, Math.ceil(count / pageSize));
  const startIdx = (currentPage - 1) * pageSize;
  const pageItems = sorted.slice(startIdx, startIdx + pageSize);

  const hasNext = currentPage < totalPages;
  const hasPrevious = currentPage > 1;

  const handlePageChange = (e) => setCurrentPage(Number(e.target.value));
  const handleNextPage = () => hasNext && setCurrentPage((p) => p + 1);
  const handlePreviousPage = () => hasPrevious && setCurrentPage((p) => p - 1);
  const handlePageSizeChange = (e) => {
    setPageSize(Number(e.target.value));
    setCurrentPage(1);
  };

  // ---- Handlers
  const handleSearchChange = (e) => {
    setSearchText(e.target.value);
    setCurrentPage(1);
  };

  const handleSort = (field) => {
    setSortConfig((prev) => {
      const isDescending = prev.ordering === field;
      return { ordering: isDescending ? `-${field}` : field };
    });
    setCurrentPage(1);
  };

  const handleSelectCustomer = (event, customer) => {
    const { checked } = event.target;
    const updated = checked
      ? [...selectedCustomers, customer]
      : selectedCustomers.filter((c) => c.id !== customer.id);

    setSelectedCustomers(updated);
    setAreAllSelected(
      updated.length > 0 && updated.length === pageItems.length
    );
  };

  const handleSelectAll = (event) => {
    const { checked } = event.target;
    setAreAllSelected(checked);
    setSelectedCustomers(checked ? [...pageItems] : []);
  };

  const handleDeleteSelected = () => {
    if (!selectedCustomers.length) {
      setToast({
        message: "لطفاً حداقل یک مشتری را انتخاب کنید.",
        type: "warn",
      });
      return;
    }
    setShowModal(true);
  };

  const handleConfirmDelete = () => {
    setShowModal(false);
    const ids = selectedCustomers.map((c) => c.id);

    setDeletedIds((prev) => [...new Set([...prev, ...ids])]);
    setSelectedCustomers([]);
    setAreAllSelected(false);

    setToast({
      message: "حذف لوکال انجام شد (برای حذف واقعی به API متصل شوید).",
      type: "success",
    });
  };

  const handleCancelDelete = () => setShowModal(false);

  return (
    <div className="mt-24 pb-20 bg-gray-50 min-h-screen font-vazir" dir="rtl">
      {/* Toast & Confirm Modal */}
      <HotToastMessage
        message={toast.message}
        type={toast.type}
        duration={3000}
        onClose={() => setToast({ message: "", type: "info" })}
        position="top"
      />
      <ConfirmModal
        isOpen={showModal}
        onConfirm={handleConfirmDelete}
        onCancel={handleCancelDelete}
      />

      {/* Container card */}
      <div className=" mx-auto px-3 sm:px-4 md:px-6">
        <div className="p-4 sm:p-6 bg-white/80 backdrop-blur-md rounded-2xl shadow-sm border border-white/40">
          {/* Top header */}
          <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
            <div className="space-y-1 text-right">
              <h2 className="text-2xl md:text-3xl font-bold text-gray-900">
                لیست مشتری‌ها
              </h2>
              <p className="text-gray-500 text-sm">
                مشاهده، جستجو و مدیریت مشتری‌های ثبت‌شده در سیستم
              </p>
            </div>

            <div className="flex flex-col items-end text-xs text-gray-500">
              <span>تعداد کل مشتری‌ها: {count}</span>
            </div>
          </div>

          {/* Search */}
          <div className="mt-4">
            <label className="block text-sm font-medium text-gray-700 text-right mb-2">
              جستجو (نام / شماره تماس / کد ملی):
            </label>
            <div className="relative">
              <input
                type="text"
                placeholder="عبارت جستجو را وارد کنید…"
                value={searchText}
                onChange={handleSearchChange}
                className="w-full pl-3 pr-10 py-3 rounded-xl border bg-white/90 focus:outline-none focus:ring-2 focus:ring-blue-500 transition text-right"
              />
              <Search className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
            </div>
          </div>

          {/* Action buttons */}
          <div className="mt-4 flex flex-wrap gap-2">
            <Link
              to="/cottage-customers-from-excel"
              className="inline-flex items-center gap-2 bg-emerald-600 text-white px-4 py-2 rounded-xl hover:bg-emerald-700 transition"
            >
              اختصاص مشتری از اکسل
            </Link>
            <button
              onClick={handleDeleteSelected}
              disabled={!selectedCustomers.length}
              className="inline-flex items-center gap-2 bg-red-600 text-white px-4 py-2 rounded-xl hover:bg-red-700 transition disabled:bg-gray-300 disabled:cursor-not-allowed"
            >
              <Trash2 className="w-4 h-4" />
              حذف انتخابی‌ها
            </button>
          </div>

          {/* Loading / Error */}
          {customersLoading && (
            <div className="mt-8 grid gap-3">
              <div className="h-10 rounded-xl bg-gray-100 animate-pulse" />
              <div className="h-10 rounded-xl bg-gray-100 animate-pulse" />
              <div className="h-10 rounded-xl bg-gray-100 animate-pulse" />
            </div>
          )}
          {customersError && !customersLoading && (
            <div className="mt-6 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-red-700 text-right">
              {String(customersError)}
            </div>
          )}

          {/* Table & Pagination */}
          {!customersLoading && !customersError && (
            <>
              {pageItems.length === 0 ? (
                <div className="mt-10 text-center text-gray-600">
                  هیچ مشتری‌ای یافت نشد.
                </div>
              ) : (
                <>
                  {/* Pagination top */}
                  <div className="mt-4">
                    <PaginationControls
                      currentPage={currentPage}
                      totalPages={totalPages}
                      pageSize={pageSize}
                      pageSizeOptions={[10, 20, 50, 100, 200]}
                      hasNext={hasNext}
                      hasPrevious={hasPrevious}
                      onPageChange={handlePageChange}
                      onNextPage={handleNextPage}
                      onPreviousPage={handlePreviousPage}
                      onPageSizeChange={handlePageSizeChange}
                    />
                  </div>

                  {/* Table */}
                  <div className="mt-3 overflow-x-auto rounded-2xl border bg-white shadow-sm">
                    <table className="min-w-full text-sm">
                      <thead className="bg-gray-50 sticky top-0">
                        <tr className="text-right text-gray-700">
                          <th className="p-3">
                            <input
                              type="checkbox"
                              onChange={handleSelectAll}
                              checked={
                                areAllSelected &&
                                pageItems.length > 0 &&
                                selectedCustomers.length === pageItems.length
                              }
                            />
                          </th>
                          <th className="p-3 font-semibold">ردیف</th>
                          <th
                            className="p-3 font-semibold cursor-pointer select-none"
                            onClick={() => handleSort("full_name")}
                          >
                            نام مشتری
                          </th>
                          <th
                            className="p-3 font-semibold cursor-pointer select-none"
                            onClick={() => handleSort("phone_number")}
                          >
                            شماره تماس
                          </th>
                          <th
                            className="p-3 font-semibold cursor-pointer select-none"
                            onClick={() => handleSort("national_code")}
                          >
                            کد ملی
                          </th>
                          <th className="p-3 font-semibold">جزئیات</th>
                        </tr>
                      </thead>
                      <tbody>
                        {pageItems.map((customer, index) => {
                          const isSelected = selectedCustomers.some(
                            (c) => c.id === customer.id
                          );

                          return (
                            <tr
                              key={customer.id}
                              className="border-t last:border-b-0 hover:bg-gray-50"
                            >
                              <td className="p-3 align-middle">
                                <input
                                  type="checkbox"
                                  checked={isSelected}
                                  onChange={(e) =>
                                    handleSelectCustomer(e, customer)
                                  }
                                />
                              </td>
                              <td className="p-3 align-middle">
                                {(currentPage - 1) * pageSize + (index + 1)}
                              </td>
                              <td className="p-3 align-middle font-medium">
                                {customer.full_name || "—"}
                              </td>
                              <td className="p-3 align-middle">
                                {customer.phone_number || "—"}
                              </td>
                              <td className="p-3 align-middle">
                                {customer.national_code || "—"}
                              </td>
                              <td className="p-3 align-middle">
                                <Link
                                  className="text-blue-600 hover:underline"
                                  to={`/customers/details/${customer.id}`}
                                >
                                  مشاهده
                                </Link>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>

                  {/* Pagination bottom */}
                  <div className="mt-3">
                    <PaginationControls
                      currentPage={currentPage}
                      totalPages={totalPages}
                      pageSize={pageSize}
                      pageSizeOptions={[10, 20, 50, 100, 200]}
                      hasNext={hasNext}
                      hasPrevious={hasPrevious}
                      onPageChange={handlePageChange}
                      onNextPage={handleNextPage}
                      onPreviousPage={handlePreviousPage}
                      onPageSizeChange={handlePageSizeChange}
                    />
                  </div>
                </>
              )}
            </>
          )}
        </div>
      </div>

      {/* Sticky bulk-actions bar */}
      {selectedCustomers.length > 0 && (
        <div className="fixed left-0 right-0 bottom-4 z-20 px-4">
          <div className="mx-auto max-w-[900px] rounded-2xl border bg-white/90 backdrop-blur p-3 shadow-lg">
            <div className="flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
              <div className="text-sm text-gray-700 text-right">
                <b>{selectedCustomers.length}</b> مشتری انتخاب شده
              </div>
              <div className="flex flex-wrap gap-2 justify-end">
                <button
                  onClick={handleDeleteSelected}
                  className="inline-flex items-center gap-2 bg-red-600 text-white px-3 py-1.5 rounded-xl hover:bg-red-700 transition"
                >
                  <Trash2 className="w-4 h-4" />
                  حذف انتخابی‌ها
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CustomerList;
